import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  COUPONS_MUTATION_KEY,
  COUPONS_QUERY_KEY,
} from '~/api/query-keys/coupons';
import { POINT_QUERY_KEY } from '~/api/query-keys/point';
import { couponsRepository } from '~/api/repository/coupons';
import type { CART_TYPE } from '~/hooks/useCartTypeParam';

// Re-export types for convenience
// Response 타입 (Model)
export type {
  BuyerCoupon,
  BuyerCouponDetail,
  BuyerCouponStatus,
  CheckoutBuyerCoupon,
  CheckoutCoupon,
  CouponDetail,
  CouponStatus,
  CouponVendor,
  DiscountTargetType,
  DiscountType,
  DownloadCouponProduct,
  DownloadedCoupon,
  IssueRule,
  PointCouponItem,
  PointCouponList,
} from '~/api/model/coupons';

// ============================================================================
// Query 훅
// ============================================================================

/** 구매자 쿠폰 목록 조회 */
// GET /api/market-place/v1/buyer-coupon
export const useBuyerCoupons = (params?: Record<string, unknown>) => {
  const { queryKey, queryFn } = COUPONS_QUERY_KEY.buyerCoupons(params);

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 구매자 쿠폰 개수 조회 */
// GET /api/market-place/v1/buyer-coupon/count
export const useBuyerCouponCount = () => {
  const { queryKey, queryFn } = COUPONS_QUERY_KEY.buyerCouponCount();

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 구매자 쿠폰 상세 조회 */
// GET /api/market-place/v1/buyer-coupon/{buyerCouponId}
export const useBuyerCouponDetail = (buyerCouponId: number) => {
  const { queryKey, queryFn } =
    COUPONS_QUERY_KEY.buyerCouponDetail(buyerCouponId);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!buyerCouponId,
  });
};

/** 체크아웃 쿠폰 조회 */
// GET /api/market-place/v1/shopping-cart/checkout/coupon
export const useCheckoutCoupons = (cartType: CART_TYPE) => {
  const { queryKey, queryFn } = COUPONS_QUERY_KEY.checkoutCoupons(cartType);

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 다운로드된 쿠폰 목록 조회 */
// GET /api/market-place/v2/coupon/download
export const useDownloadedCoupons = (couponIds: number[]) => {
  const { queryKey, queryFn } = COUPONS_QUERY_KEY.downloadedCoupons(couponIds);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: couponIds.length > 0,
  });
};

/** 포인트 쿠폰 목록 조회 */
// GET /api/market-place/v1/coupon/point
export const usePointCoupons = () => {
  const { queryKey, queryFn } = COUPONS_QUERY_KEY.pointCoupons();

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 쿠폰 상세 조회 */
// GET /api/market-place/v1/coupon/{couponId}
export const useCouponDetail = (couponId: number) => {
  const { queryKey, queryFn } = COUPONS_QUERY_KEY.detail(couponId);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!couponId,
  });
};

// ============================================================================
// Mutation 훅
// ============================================================================

/** 전체 쿠폰 다운로드 */
// POST /api/market-place/v1/coupon/download/issue
export const useDownloadAllCoupons = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COUPONS_MUTATION_KEY.downloadAll(),
    mutationFn: (couponIds: number[]) =>
      couponsRepository.downloadAllCoupons(couponIds),
    onSuccess: () => {
      void queryClient.invalidateQueries(COUPONS_QUERY_KEY.all);
    },
  });
};

/** 단일 쿠폰 다운로드 */
// POST /api/market-place/v1/coupon/download/{couponId}/issue
export const useDownloadCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (couponId: number) =>
      couponsRepository.downloadCoupon(couponId),
    onSuccess: (_, couponId) => {
      void queryClient.invalidateQueries(
        COUPONS_MUTATION_KEY.download(couponId),
      );
      void queryClient.invalidateQueries(COUPONS_QUERY_KEY.all);
    },
  });
};

/** 포인트 쿠폰 발급 */
// POST /api/market-place/v1/coupon/point/{couponId}/issue
export const useIssuePointCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (couponId: number) =>
      couponsRepository.issuePointCoupon(couponId),
    onSuccess: (_, couponId) => {
      void queryClient.invalidateQueries(
        COUPONS_MUTATION_KEY.issuePoint(couponId),
      );
      void queryClient.invalidateQueries(COUPONS_QUERY_KEY.all);
      void queryClient.invalidateQueries(POINT_QUERY_KEY.all);
    },
  });
};
